import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { type AuthUser, type AuthTokens, getStoredTokens, fetchMe, clearTokens, logout, refreshSession } from "./auth";

interface AuthState {
  user: AuthUser | null;
  tokens: AuthTokens | null;
  loading: boolean;
  setAuth: (tokens: AuthTokens) => Promise<void>;
  signOut: () => void;
}

const AuthContext = createContext<AuthState>({
  user: null,
  tokens: null,
  loading: true,
  setAuth: async () => {},
  signOut: () => {},
});

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [tokens, setTokens] = useState<AuthTokens | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const stored = getStoredTokens();
    if (!stored?.id_token) {
      setLoading(false);
      return;
    }

    fetchMe(stored.id_token)
      .then((u) => {
        setUser(u);
        setTokens(stored);
      })
      .catch(async () => {
        if (stored.refresh_token) {
          try {
            const refreshed = await refreshSession();
            if (refreshed) {
              const u = await fetchMe(refreshed.id_token);
              setUser(u);
              setTokens(refreshed);
              return;
            }
          } catch { /* fall through to clear */ }
        }
        clearTokens();
      })
      .finally(() => setLoading(false));
  }, []);

  const setAuth = async (newTokens: AuthTokens) => {
    setTokens(newTokens);
    const u = await fetchMe(newTokens.id_token);
    setUser(u);
  };

  const signOut = () => {
    setUser(null);
    setTokens(null);
    logout();
  };

  return (
    <AuthContext.Provider value={{ user, tokens, loading, setAuth, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
